/**
 * DEC-1148 (FEEDBACK-4): the form inside the Feedback panel.
 *
 * SINGLE SCREEN, NOT A CHAT. The ticket is explicit and it's worth restating
 * because the surrounding panel is chat-styled: one static DECO intro bubble,
 * then a plain form. No simulated conversation, no typing indicators. A fake
 * chat implies someone is reading in real time; nobody is, and pretending
 * otherwise is the fastest way to lose a beta user's trust.
 *
 * DISCLOSURE IS HONEST AND ALWAYS EXPANDED (founder decision 2). The copy says
 * attached logs "may occasionally include personal details" — it does NOT
 * promise "never your personal information", because the scrub is defense in
 * depth, not a guarantee (DEC-1145). Making that promise would be the one
 * sentence here that could actually be false.
 */

import React, { useEffect, useRef, useState } from 'react';

export const CATEGORIES = [
  { value: 'bug', label: 'Something’s broken', severity: true },
  { value: 'confusing', label: 'Something’s confusing', severity: true },
  { value: 'suggestion', label: 'I have an idea', severity: false },
  { value: 'other', label: 'Something else', severity: false },
];

export const SEVERITIES = [
  { value: 'blocking', label: 'I’m stuck — I can’t continue' },
  { value: 'friction', label: 'Annoying, but I found a way around' },
  { value: 'fyi', label: 'Just letting you know' },
];

const chipStyle = (selected) => ({
  minHeight: '44px',
  padding: '10px 14px',
  borderRadius: '8px',
  border: selected ? '2px solid #2196f3' : '2px solid var(--medium-gray)',
  background: selected ? 'rgba(33, 150, 243, 0.1)' : 'white',
  color: selected ? '#2196f3' : 'var(--text-primary)',
  fontWeight: selected ? 600 : 400,
  fontSize: '13px',
  cursor: 'pointer',
  textAlign: 'left',
});

const FeedbackForm = ({
  fields, onChange, onSubmit, submitting, error, pageLabel, isMobile,
  restoredDraft, onDiscardDraft,
}) => {
  const [touched, setTouched] = useState(false);
  const bodyRef = useRef(null);

  const showSeverity = CATEGORIES.find((c) => c.value === fields.category)?.severity;
  const bodyInvalid = touched && !fields.body.trim();

  // "What happened?" sits at the TOP of the form and takes focus, because it
  // is the only required field — a user who types just that and submits has
  // given us something useful.
  useEffect(() => {
    if (!restoredDraft) bodyRef.current?.focus();
  }, [restoredDraft]);

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!fields.body.trim()) {
      bodyRef.current?.focus();
      return;
    }
    onSubmit();
  };

  return (
    <form onSubmit={submit} noValidate>
      {restoredDraft && (
        <div
          role="status"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
            padding: '8px 12px', marginBottom: '14px', fontSize: '13px', color: '#1e40af',
          }}
        >
          <span>Restored your unsent draft</span>
          <button
            type="button"
            onClick={onDiscardDraft}
            style={{ background: 'none', border: 0, color: '#1e40af', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px' }}
          >
            Start over
          </button>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="feedback-body">What happened? *</label>
        <textarea
          id="feedback-body"
          ref={bodyRef}
          value={fields.body}
          onChange={(e) => onChange({ body: e.target.value })}
          // On mobile the keyboard covers the lower half of a bottom sheet;
          // scroll the field into view so the user can see what they type.
          onFocus={() => { if (isMobile) bodyRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }}
          rows={4}
          maxLength={2000}
          aria-required="true"
          aria-invalid={bodyInvalid || undefined}
          aria-describedby={bodyInvalid ? 'feedback-body-error' : undefined}
          placeholder="Tell us what you were doing and what went wrong."
          style={{ width: '100%', minHeight: '96px', resize: 'vertical' }}
        />
        {bodyInvalid && (
          <div id="feedback-body-error" role="alert" style={{ color: '#dc3545', fontSize: '13px', marginTop: '4px' }}>
            Please tell us what happened.
          </div>
        )}
      </div>

      <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
        <legend style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '8px', padding: 0 }}>
          What kind of thing is it?
        </legend>
        {/* Real radios, visually chipped — a div-with-onClick would lose arrow-key
            navigation and the group semantics screen readers rely on. */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
          {CATEGORIES.map((c) => (
            <label key={c.value} style={{ ...chipStyle(fields.category === c.value), display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="radio"
                name="feedback-category"
                value={c.value}
                checked={fields.category === c.value}
                onChange={() => onChange({ category: c.value, severity: null })}
                style={{ margin: 0 }}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {showSeverity && (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '8px', padding: 0 }}>
            How much is it getting in your way?
          </legend>
          <div style={{ display: 'grid', gap: '8px' }}>
            {SEVERITIES.map((s) => (
              <label key={s.value} style={{ ...chipStyle(fields.severity === s.value), display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="radio"
                  name="feedback-severity"
                  value={s.value}
                  checked={fields.severity === s.value}
                  onChange={() => onChange({ severity: s.value })}
                  style={{ margin: 0 }}
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className="form-group">
        <label htmlFor="feedback-expected">What did you expect instead? <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
        <textarea
          id="feedback-expected"
          value={fields.expected}
          onChange={(e) => onChange({ expected: e.target.value })}
          rows={2}
          maxLength={1000}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>

      {/* Context + disclosure. Always expanded, never behind a "details"
          disclosure triangle — a consent notice nobody opens isn't consent. */}
      <div style={{ background: 'var(--light-gray)', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text-primary)', marginBottom: '8px' }}>
          You’re on <strong>{pageLabel}</strong>.
        </div>
        <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!fields.attachTechnical}
            onChange={(e) => onChange({ attachTechnical: !e.target.checked })}
            style={{ marginTop: '2px' }}
          />
          <span>
            Don’t attach technical details.{' '}
            {fields.attachTechnical
              ? 'We’ll include a log of recent errors from this session to help us fix the problem — this technical information may occasionally include personal details.'
              : 'We won’t include any error logs, which may make the problem harder for us to find.'}
          </span>
        </label>
      </div>

      {error && (
        <div role="alert" style={{ background: '#fff5f5', border: '1px solid #f5c2c7', borderRadius: '6px', padding: '12px', marginBottom: '14px', fontSize: '13px', color: '#842029' }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        className="btn btn-primary"
        disabled={submitting}
        style={{ width: '100%', minHeight: '44px' }}
      >
        {submitting ? 'Sending…' : 'Send to DECO'}
      </button>
    </form>
  );
};

export default FeedbackForm;
